import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { getCompanyConfiguration, updateCompanyConfiguration } from '../../../services/companyService';
import { fetchAllCurrencies, formatCurrencyOptions, detectUserCurrency } from '../../../services/currencyService';

const ConfigurationModal = ({ isOpen, onClose, onSubmit, company }) => {
  const [formData, setFormData] = useState({
    fiscalYearStart: 'January',
    baseCurrency: 'USD',
    taxJurisdiction: 'United States',
    accountingMethod: 'Accrual',
    dateFormat: 'MM/DD/YYYY',
    timeZone: 'America/New_York'
  });
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadCurrencies();
    }
  }, [isOpen]);

  useEffect(() => {
    if (company && isOpen) {
      loadConfiguration();
    }
  }, [company, isOpen]);

  const loadCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      const currencies = await fetchAllCurrencies();
      const options = formatCurrencyOptions(currencies);
      setCurrencyOptions(options);
    } catch (error) {
      console.error('Error loading currencies:', error);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const loadConfiguration = async () => {
    setLoadingConfig(true);
    try {
      const config = await getCompanyConfiguration(company?.id);
      if (config) {
        setFormData({
          fiscalYearStart: config?.fiscalYearStart || 'January',
          baseCurrency: config?.baseCurrency || 'USD',
          taxJurisdiction: config?.taxJurisdiction || 'United States',
          accountingMethod: config?.accountingMethod || 'Accrual',
          dateFormat: config?.dateFormat || 'MM/DD/YYYY',
          timeZone: config?.timeZone || 'America/New_York'
        });
      } else {
        // Auto-detect currency for new configuration
        const detectedCurrency = await detectUserCurrency();
        if (detectedCurrency) {
          setFormData(prev => ({ ...prev, baseCurrency: detectedCurrency }));
        }
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const monthOptions = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const accountingMethodOptions = [
    { value: 'Accrual', label: 'Accrual' },
    { value: 'Cash', label: 'Cash' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' }
  ];

  const timeZoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await updateCompanyConfiguration(company?.id, formData);
      onSubmit();
      onClose();
    } catch (error) {
      console.error('Error updating configuration:', error);
      alert('Failed to update configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Company Configuration</h2>
            <p className="text-sm text-muted-foreground mt-1">{company?.businessName}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        {loadingConfig ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading configuration...</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Accounting Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Fiscal Year Start"
                    value={formData?.fiscalYearStart}
                    onChange={(value) => setFormData({ ...formData, fiscalYearStart: value })}
                    options={monthOptions}
                  />
                  <Select
                    label="Accounting Method"
                    value={formData?.accountingMethod}
                    onChange={(value) => setFormData({ ...formData, accountingMethod: value })}
                    options={accountingMethodOptions}
                  />
                </div>
              </div>

              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Regional Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Base Currency"
                    value={formData?.baseCurrency}
                    onChange={(value) => setFormData({ ...formData, baseCurrency: value })}
                    options={currencyOptions}
                    disabled={loadingCurrencies}
                  />
                  <Input
                    label="Tax Jurisdiction"
                    value={formData?.taxJurisdiction}
                    onChange={(e) => setFormData({ ...formData, taxJurisdiction: e?.target?.value })}
                    placeholder="Enter tax jurisdiction"
                  />
                </div>
                {loadingCurrencies && (
                  <p className="text-sm text-muted-foreground mt-2">Loading global currencies...</p>
                )}
              </div>

              <div>
                <h3 className="font-heading font-semibold text-base text-foreground mb-4">Display Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Date Format"
                    value={formData?.dateFormat}
                    onChange={(value) => setFormData({ ...formData, dateFormat: value })}
                    options={dateFormatOptions}
                  />
                  <Select
                    label="Time Zone"
                    value={formData?.timeZone}
                    onChange={(value) => setFormData({ ...formData, timeZone: value })}
                    options={timeZoneOptions}
                  />
                </div>
              </div>

              <div className="bg-info/10 rounded-lg p-4 border border-info/20">
                <p className="text-sm text-info-foreground">
                  <strong>Note:</strong> Changes to accounting settings may affect existing financial data. Please consult with your accountant before making significant changes.
                </p>
              </div>
            </div>
          </form>
        )}

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading || loadingConfig}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={loading || loadingConfig}
            iconName={loading ? 'Loader2' : 'Save'}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationModal;
